import * as React from 'react';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';

import { insertDriftChatBot } from '@coursera-web/growth-upsell';

import useRouter from 'js/lib/useRouter';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import { SLUGS_TO_PRE_LOAD_IMAGE } from 'bundles/ent-website/constants/layout';
import { LEAN_DATA_CALENDAR_LOCAL_STORAGE_KEY } from 'bundles/ent-website/constants/marketo';
import DataSourceLoader from 'bundles/ent-website/contentful/core/DataSourceLoader';
import Module from 'bundles/ent-website/contentful/core/Module';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { BlockAcademyModule } from 'bundles/ent-website/contentful/modules/AcademyModule/AcademyModule';
import { BlockBenefitListModule } from 'bundles/ent-website/contentful/modules/BenefitListModule';
import { BlockCTAModule } from 'bundles/ent-website/contentful/modules/CTAModule';
import { BlockCaseStudyModule } from 'bundles/ent-website/contentful/modules/CaseStudyModule';
import { BlockComparePlansModule } from 'bundles/ent-website/contentful/modules/ComparePlansModule';
import { BlockContactSalesModule } from 'bundles/ent-website/contentful/modules/ContactSalesModule';
import { BlockContentModule } from 'bundles/ent-website/contentful/modules/ContentModule';
import { BlockCourseSearchWidget } from 'bundles/ent-website/contentful/modules/CourseSearchWidget';
import { BlockEnterpriseArticle } from 'bundles/ent-website/contentful/modules/EntArticleModule';
import { BlockHeroModule } from 'bundles/ent-website/contentful/modules/EntWebsiteHero';
import { BlockExternalEmbedWidget } from 'bundles/ent-website/contentful/modules/ExternalEmbedWidget';
import { BlockFAQModule } from 'bundles/ent-website/contentful/modules/FAQModule';
import { BlockFeaturedContentModule } from 'bundles/ent-website/contentful/modules/FeaturedContentModule';
import { BlockHowModule } from 'bundles/ent-website/contentful/modules/HowModule';
import { BlockInsightsModule } from 'bundles/ent-website/contentful/modules/InsightsModule';
import { BlockLeaderListModule } from 'bundles/ent-website/contentful/modules/LeaderListModule';
import { BlockMarketoModule } from 'bundles/ent-website/contentful/modules/MarketoModule';
import { BlockNative } from 'bundles/ent-website/contentful/modules/Native';
import { BlockPartnerListModule } from 'bundles/ent-website/contentful/modules/PartnerListModule';
import { BlockPdfViewer } from 'bundles/ent-website/contentful/modules/PdfViewerModule';
import { BlockProspectNav } from 'bundles/ent-website/contentful/modules/ProspectNav';
import { BlockResourceHub } from 'bundles/ent-website/contentful/modules/ResourceHub';
import { BlockResourceHubV2 } from 'bundles/ent-website/contentful/modules/ResourceHubV2';
import { BlockSkillsetsModule } from 'bundles/ent-website/contentful/modules/SkillsetsModule';
import { BlockTabsModule } from 'bundles/ent-website/contentful/modules/TabsModule';
import { EntWebsiteTestimonialCarousel } from 'bundles/ent-website/contentful/modules/TestimonialCarouselModule';
import { BlockTestimonialModule } from 'bundles/ent-website/contentful/modules/TestimonialModule';
import { BlockTwoColumnsModule } from 'bundles/ent-website/contentful/modules/TwoColumnsModule';
import { BlockUpcomingEventsModule } from 'bundles/ent-website/contentful/modules/UpcomingEventsModule';
import { BlockValuePropModule } from 'bundles/ent-website/contentful/modules/ValuePropModule';
import { BlockWhyModule } from 'bundles/ent-website/contentful/modules/WhyModule';
import usePageAssemblyQuery from 'bundles/ent-website/queries/hooks/usePageAssemblyQuery';
import type { Namespaces } from 'bundles/epic/__generated__/namespaces';
import EnterpriseWebsiteExperiments from 'bundles/epic/clients/EnterpriseWebsite';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import _t from 'i18n!nls/ent-website';

type Props = {
  slug: string;
  domain: string;
};

export default function PageAssembly({ slug, domain }: Props) {
  const { title, blocks, dataSources, seoDescription, seoImageUrl, seoCanonical, preventSearchEngine, seoTitle } =
    usePageAssemblyQuery(slug, domain);
  // WARN: DO NOT call `React.useContext(NativeDataContext)` in `PageAssembly`!!.
  // This page renders the data sources.
  // If you call `React.useContext(NativeDataContext)` here,
  // it will cause an infinite re-render loop.
  const router = useRouter();

  useEffect(() => {
    if (EnterpriseWebsiteExperiments.get('enableDriftChatbot2')) {
      insertDriftChatBot(window);
    }
  }, []);

  // Temp LeanData Calendar Experiment
  useEffect(() => {
    const shouldShowLeanDataCalendar = localStorageEx.getItem(LEAN_DATA_CALENDAR_LOCAL_STORAGE_KEY, String, 'false');
    localStorageEx.removeItem(LEAN_DATA_CALENDAR_LOCAL_STORAGE_KEY);

    // Code provided by LeanData
    if (shouldShowLeanDataCalendar !== 'true') return;
    (function (provider) {
      // eslint-disable-next-line no-console
      console.log('loading popupModal snippet');
      const LDBookItPopupObjName = 'LDBookItPopup';
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      if (!window[LDBookItPopupObjName]) window[LDBookItPopupObjName] = {};
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      window[LDBookItPopupObjName].instantLoad = true;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      window[LDBookItPopupObjName].provider = provider;
      const scriptElement = document.createElement('script');
      const targetScript = document.getElementsByTagName('script')[0];
      const targetHead = document.getElementsByTagName('head')[0];
      scriptElement.async = true;
      scriptElement.src = `https://app.leandata.com/js-snippet/ld-book-popup.js`;
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      if (targetScript.parentNode) {
        // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        targetScript.parentNode.insertBefore(scriptElement, targetScript);
      } else if (targetHead) {
        targetHead.append(scriptElement);
      }
    })('marketo');
  }, []);

  const path = router.location.pathname;

  let suffix = _t('Coursera');

  if (path.startsWith('/business/') || path === '/business') {
    suffix = _t('Coursera for Business');
  } else if (path.startsWith('/government/') || path === '/government') {
    suffix = _t('Coursera for Government');
  }

  const dynamicPageTitle = `${seoTitle || title} | ${suffix}`;

  return (
    <div>
      <CourseraMetatags
        metaNameAndProperties={{
          title: dynamicPageTitle,
          ogAndTwitterTitle: dynamicPageTitle,
          disableCourseraSuffix: true,
          description: seoDescription,
          disableCourseraDescriptionSuffix: !!seoDescription,
          absoluteImageHref: seoImageUrl,
        }}
        paginationProperties={{ canonicalLinkHrefOverride: seoCanonical ?? undefined }}
      />

      {preventSearchEngine && <Helmet meta={[{ name: 'robots', content: 'noindex, nofollow' }]} />}

      <DataSourceLoader dataSources={dataSources} />

      {blocks.map((b) => {
        let el;

        const { experimentName, experimentValue: expectedExperimentValue } = b;
        const stringExperimentValue = String(
          EnterpriseWebsiteExperiments.get(experimentName as keyof Namespaces['EnterpriseWebsite'])
        );
        if (experimentName && stringExperimentValue !== expectedExperimentValue) {
          return null;
        }

        switch (b.__typename) {
          case 'EntWebsiteBlockAssembly':
            switch (b.reference?.__typename) {
              case 'EnterpriseArticle':
                el = <BlockEnterpriseArticle blockId={b.sys.id} />;
                break;
              case 'EntWebsiteTabs':
                el = <BlockTabsModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteUpcomingEvents':
                el = <BlockUpcomingEventsModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteFeaturedContent':
                el = <BlockFeaturedContentModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteResourceHubCollectionWithSearch':
                el = <BlockResourceHubV2 blockId={b.sys.id} />;
                break;
              case 'EntWebsiteTwoColumns':
                el = <BlockTwoColumnsModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteInsights':
                el = <BlockInsightsModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteHow':
                el = <BlockHowModule preLoadHeroImage={SLUGS_TO_PRE_LOAD_IMAGE.includes(slug)} blockId={b.sys.id} />;
                break;
              case 'EntWebsiteHero':
                el = <BlockHeroModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteWhy':
                el = <BlockWhyModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteTestimonial':
                el = <BlockTestimonialModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteTestimonialCarousel':
                el = <EntWebsiteTestimonialCarousel blockId={b.sys.id} />;
                break;
              case 'EntWebsitePartnerList':
                el = <BlockPartnerListModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteCtaBlock':
                el = <BlockCTAModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteBenefitList':
                el = <BlockBenefitListModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteAcademyModule':
                el = <BlockAcademyModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteMarketoModule':
                el = <BlockMarketoModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteLeaderList':
                el = <BlockLeaderListModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteComparePlans':
                el = <BlockComparePlansModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteSkillsets':
                el = <BlockSkillsetsModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteContactSales':
                el = <BlockContactSalesModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteCaseStudy':
                el = <BlockCaseStudyModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteContent':
                el = <BlockContentModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteValuePropModule':
                el = <BlockValuePropModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteProspectNav':
                el = <BlockProspectNav blockId={b.sys.id} />;
                break;
              case 'EntWebsiteResourceHub':
                el = <BlockResourceHub blockId={b.sys.id} />;
                break;
              case 'EntWebsiteCourseSearchWidget':
                el = <BlockCourseSearchWidget blockId={b.sys.id} />;
                break;
              case 'EntWebsiteFaq':
                el = <BlockFAQModule blockId={b.sys.id} />;
                break;
              case 'EntWebsiteCerosWidget':
                el = <BlockExternalEmbedWidget blockId={b.sys.id} />;
                break;
              case 'EntWebsitePdfViewer':
                el = <BlockPdfViewer blockId={b.sys.id} />;
                break;
              case 'EntWebsiteNative':
                el = <BlockNative blockId={b.sys.id} />;
                break;
              default:
                return null;
            }
            break;

          default:
            return null;
        }

        return (
          <Module content={b.reference} key={b.sys.id}>
            {el}
          </Module>
        );
      })}
    </div>
  );
}
